import React, { useEffect, useMemo, useRef, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import axios from "axios";
import {
  BadgeCheck,
  Check,
  Loader2,
  Newspaper,
  Pencil,
  Printer,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { base_url } from "../../config";
import {
  darkBodyCellSx,
  darkHeadCellSx,
  darkMrtTheme,
  darkPaperSx,
  darkPinnedBodyCellSx,
  darkPinnedHeadCellSx,
  darkRowSx,
  darkSearchSx,
  darkToolbarSx,
} from "../Codes/darkTable";

type Events = {
  reception: boolean;
  studentReception: boolean;
  banquet: boolean;
  industryDay: boolean;
  diversityLunch: boolean;
};

type Pass = {
  /** The P-code printed on the badge. Every endpoint here keys on it. */
  id: string;
  uuid: string;
  name: string;
  affiliation: string;
  category: string;
  events: Events;
  /** Door labels, resolved server-side the same way the scanner resolves them. */
  admits: string[];
  otherAccess: string | null;
  badge_printed: boolean;
};

/**
 * The five columns on ijcai25_extra_passes, and which of them the doors read.
 *
 * industry_day has no door — it is a programme day, not a social event — so the scanner
 * ignores it and the grid says so rather than implying a badge will open something.
 */
const EVENT_FIELDS: {
  key: keyof Events;
  column: string;
  label: string;
  door: boolean;
}[] = [
  { key: "reception", column: "reception", label: "Welcome Reception", door: true },
  { key: "studentReception", column: "student_reception", label: "Student Reception", door: true },
  { key: "banquet", column: "banquet", label: "Conference Banquet", door: true },
  { key: "diversityLunch", column: "diversity_and_lunch", label: "D&I Lunch", door: true },
  { key: "industryDay", column: "industry_day", label: "Industry Day", door: false },
];

const EMPTY_EVENTS: Events = {
  reception: false,
  studentReception: false,
  banquet: false,
  industryDay: false,
  diversityLunch: false,
};

const inputClass =
  "w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-1.5 text-sm text-slate-100 " +
  "placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-transparent";

const saveBlob = (data: BlobPart, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const Modal: React.FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, subtitle, onClose, children }) => (
  <div
    className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/70 p-4 py-10"
    onClick={onClose}
  >
    <div
      className="w-full max-w-xl rounded-2xl border border-slate-700/60 bg-slate-900 shadow-2xl"
      onClick={(event) => event.stopPropagation()}
    >
      <header className="flex items-start justify-between gap-3 border-b border-slate-700/50 px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
          {subtitle && (
            <p className="truncate font-mono text-[11px] text-slate-500">{subtitle}</p>
          )}
        </div>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-200">
          <X className="h-4 w-4" />
        </button>
      </header>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

/** The badge for one pass: whether it has been printed, and printing it again. */
const BadgeModal: React.FC<{
  pass: Pass;
  onClose: () => void;
  onChange: (pass: Pass) => void;
}> = ({ pass, onClose, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");

  const togglePrinted = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await axios.post(`${base_url}/api/admin/update_extra_pass`, {
        id: pass.id,
        badge_printed: !pass.badge_printed,
      });
      onChange(response.data.pass);
    } catch {
      setError("Could not change the printed flag.");
    } finally {
      setBusy(false);
    }
  };

  /** Printing never sets the flag — reprinting a lost badge must not depend on it. */
  const print = async () => {
    setPrinting(true);
    setError("");
    try {
      const response = await axios.post(
        `${base_url}/api/admin/print_press_badge_mt`,
        { id: pass.id },
        { responseType: "blob" }
      );
      saveBlob(response.data, `badge_${pass.id}.pdf`);
    } catch {
      setError("Could not build the badge PDF.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal title="Badge" subtitle={pass.id} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <div className="text-sm text-slate-100">{pass.name}</div>
          {pass.affiliation && (
            <div className="text-xs text-slate-400">{pass.affiliation}</div>
          )}
          <div className="mt-1 text-[11px] text-blue-300">{pass.category}</div>
        </div>

        <div className="rounded-lg border border-slate-700/50 bg-slate-800/40 p-2.5">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">
            The QR opens
          </div>
          {pass.admits.length ? (
            <div className="flex flex-wrap gap-1">
              {pass.admits.map((label) => (
                <span
                  key={label}
                  className="rounded-md border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-200"
                >
                  {label}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500">
              Nothing — every social event door will refuse this badge.
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={togglePrinted}
          disabled={busy}
          className={`flex w-full items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-xs transition-colors disabled:opacity-50 ${
            pass.badge_printed
              ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
              : "border-slate-600/50 bg-slate-800/60 text-slate-300 hover:bg-slate-700/60"
          }`}
        >
          <span className="font-medium">
            {pass.badge_printed ? "Printed" : "Not printed"}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px]">
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>
                {pass.badge_printed && <Check className="h-3.5 w-3.5" />}
                {pass.badge_printed ? "mark as not printed" : "mark as printed"}
              </>
            )}
          </span>
        </button>

        <button
          type="button"
          onClick={print}
          disabled={printing}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-blue-500/40 bg-blue-500/25 px-3 py-2 text-xs font-medium text-blue-100 transition-colors hover:bg-blue-500/35 disabled:opacity-50"
        >
          {printing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Printer className="h-3.5 w-3.5" />
          )}
          {pass.badge_printed ? "Print again" : "Print badge"}
        </button>

        <p className="text-[10px] leading-snug text-slate-500">
          Printing does not change the flag — reprinting a lost badge must not depend on it.
          Bulk printing does mark them.
        </p>

        {error && <div className="text-[11px] text-red-300">{error}</div>}
      </div>
    </Modal>
  );
};

/** Create a pass, or change one — including what its badge opens. */
const PassModal: React.FC<{
  pass: Pass | null;
  categories: string[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ pass, categories, onClose, onSaved }) => {
  const [name, setName] = useState(pass?.name ?? "");
  const [affiliation, setAffiliation] = useState(pass?.affiliation ?? "");
  const [category, setCategory] = useState(pass?.category ?? "");
  const [otherAccess, setOtherAccess] = useState(pass?.otherAccess ?? "");
  const [events, setEvents] = useState<Events>(pass?.events ?? EMPTY_EVENTS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Ref guard as well as state: a second click can land before React has re-rendered
  // with the button disabled, and would fire a duplicate POST.
  const inFlight = useRef(false);

  const submit = async (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault();
    if (inFlight.current || !name.trim() || !category.trim()) return;
    inFlight.current = true;
    setSaving(true);
    setError("");

    try {
      if (pass) {
        await axios.post(`${base_url}/api/admin/update_extra_pass`, {
          id: pass.id,
          name: name.trim(),
          affiliation: affiliation.trim(),
          category: category.trim(),
          other_access: otherAccess.trim() || null,
          reception: events.reception,
          student_reception: events.studentReception,
          banquet: events.banquet,
          industry_day: events.industryDay,
          diversity_and_lunch: events.diversityLunch,
        });
      } else {
        await axios.post(`${base_url}/api/admin/post_extra`, {
          name: name.trim(),
          affiliation: affiliation.trim(),
          category: category.trim(),
          otherAccess: otherAccess.trim() || null,
          events,
        });
      }
      onSaved();
      onClose();
    } catch {
      setError("Could not save the pass.");
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      title={pass ? "Edit pass" : "New pass"}
      subtitle={pass?.id}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-400">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-400">
              Affiliation
            </label>
            <input
              value={affiliation}
              onChange={(e) => setAffiliation(e.target.value)}
              placeholder="Company, institute…"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-400">
            Category — printed across the badge
          </label>
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="PRESS"
            required
            list="pass-categories"
            className={inputClass}
          />
          <datalist id="pass-categories">
            {categories.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          <p className="mt-1 text-[10px] text-slate-500">
            Long ones are shrunk to fit the badge rather than overflowing it.
          </p>
        </div>

        <div>
          <label className="mb-2 block text-xs font-medium text-slate-400">
            What the badge opens
          </label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {EVENT_FIELDS.map((field) => {
              const checked = events[field.key];
              return (
                <label
                  key={field.key}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                    checked
                      ? "border-blue-500/30 bg-blue-500/15 text-blue-200"
                      : "border-slate-700/50 bg-slate-900/60 text-slate-300 hover:border-slate-600/70"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      setEvents((prev) => ({ ...prev, [field.key]: e.target.checked }))
                    }
                    className="h-3.5 w-3.5 accent-blue-500"
                  />
                  <span className="flex-1">{field.label}</span>
                  {!field.door && (
                    <span className="text-[10px] text-slate-500">no door</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-400">
            Other access — a note, not enforced anywhere
          </label>
          <input
            value={otherAccess ?? ""}
            onChange={(e) => setOtherAccess(e.target.value)}
            placeholder="Opening Ceremony, Closing Ceremony"
            className={inputClass}
          />
        </div>

        {error && <div className="text-[11px] text-red-300">{error}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-600/40 bg-slate-700/40 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-700/70"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim() || !category.trim()}
            className="inline-flex items-center gap-1.5 rounded-md border border-blue-500/30 bg-blue-500/20 px-3 py-1.5 text-xs font-medium text-blue-200 transition-colors hover:bg-blue-500/30 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {pass ? "Save" : "Create pass"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const PressPasses = () => {
  const [passes, setPasses] = useState<Pass[]>([]);
  const [loading, setLoading] = useState(true);
  const [badgeFor, setBadgeFor] = useState<Pass | null>(null);
  const [editing, setEditing] = useState<Pass | null>(null);
  const [creating, setCreating] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [notice, setNotice] = useState("");

  const load = async () => {
    try {
      const response = await axios.get(`${base_url}/api/admin/get_extra`);
      setPasses(response.data || []);
    } catch (error) {
      console.error("Error fetching extra passes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Only for the category suggestions in the pass modal — the table filters itself
  // through its own search box.
  const categories = useMemo(
    () => Array.from(new Set(passes.map((pass) => pass.category))).sort((a, b) => a.localeCompare(b)),
    [passes]
  );

  const unprinted = passes.filter((pass) => !pass.badge_printed).length;

  const replacePass = (updated: Pass) => {
    setPasses((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    setBadgeFor((current) => (current && current.id === updated.id ? updated : current));
  };

  const remove = async (pass: Pass) => {
    if (!window.confirm(`Delete the pass ${pass.id} for ${pass.name}?`)) return;
    try {
      await axios.post(`${base_url}/api/admin/remove_extra`, { id: pass.id });
      setPasses((prev) => prev.filter((item) => item.id !== pass.id));
    } catch (error) {
      console.error("Error deleting pass:", error);
    }
  };

  /**
   * Print every unprinted badge. The server bounds each response and
   * reports what is left, so ask again until nothing remains.
   */
  const bulkPrint = async () => {
    if (bulk) return;
    setBulk(true);
    setNotice("");
    let part = 0;
    let printed = 0;
    try {
      for (;;) {
        part += 1;
        const response = await axios.post(
          `${base_url}/api/admin/print_extra_passes`,
          {
            only_unprinted: true,
          },
          { responseType: "blob" }
        );
        // An empty batch comes back as JSON rather than a PDF.
        if (response.data.type === "application/json") break;

        const count = Number(response.headers["x-printed"] || 0);
        const remaining = Number(response.headers["x-remaining"] || 0);
        printed += count;
        saveBlob(response.data, `extra_passes_${String(part).padStart(2, "0")}.pdf`);
        if (!remaining || !count) break;
      }
      setNotice(`${printed} badge(s) printed in ${part} file(s) and marked as printed.`);
      await load();
    } catch (error) {
      console.error("Error printing badges:", error);
      setNotice("Printing stopped early — see the console.");
    } finally {
      setBulk(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Pass",
        size: 100,
        Cell: ({ row }: { row: { original: Pass } }) => (
          <span className="rounded-md border border-slate-600/40 bg-slate-700/40 px-2 py-0.5 font-mono text-[11px] text-slate-100">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 200,
        Cell: ({ row }: { row: { original: Pass } }) => (
          <div className="min-w-0">
            <div className="truncate">{row.original.name}</div>
            <div className="truncate text-[11px] text-slate-500">
              {row.original.affiliation}
            </div>
          </div>
        ),
      },
      { accessorKey: "category", header: "Category", size: 230 },
      {
        id: "admits",
        header: "Opens",
        size: 240,
        accessorFn: (row: Pass) => row.admits.join(", "),
        Cell: ({ row }: { row: { original: Pass } }) =>
          row.original.admits.length ? (
            <div className="flex flex-wrap gap-1">
              {row.original.admits.map((label) => (
                <span
                  key={label}
                  className="rounded-md border border-emerald-500/30 bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-200"
                >
                  {label}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-slate-600">—</span>
          ),
      },
      {
        id: "printed",
        header: "Printed",
        size: 90,
        accessorFn: (row: Pass) => (row.badge_printed ? "yes" : "no"),
        Cell: ({ row }: { row: { original: Pass } }) =>
          row.original.badge_printed ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
              <Check className="h-3.5 w-3.5" /> printed
            </span>
          ) : (
            <span className="text-xs text-slate-600">—</span>
          ),
      },
      {
        id: "actions",
        header: "Badge",
        enableSorting: false,
        enableColumnActions: false,
        enableHiding: false,
        size: 130,
        muiTableHeadCellProps: { sx: { ...darkHeadCellSx, ...darkPinnedHeadCellSx } },
        muiTableBodyCellProps: { sx: { ...darkBodyCellSx, ...darkPinnedBodyCellSx } },
        Cell: ({ row }: { row: { original: Pass } }) => (
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => setBadgeFor(row.original)}
              title="Badge"
              className="inline-flex h-7 items-center justify-center gap-1 rounded-md border border-blue-500/30 bg-blue-500/15 px-2 text-[11px] text-blue-300 transition-colors hover:bg-blue-500/25"
            >
              <BadgeCheck className="h-3.5 w-3.5" />
              Badge
            </button>
            <button
              onClick={() => setEditing(row.original)}
              title="Edit this pass"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-700/60 hover:text-slate-200"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => remove(row.original)}
              title="Delete this pass"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-red-500/15 hover:text-red-300"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-[1500px] space-y-4 px-4 py-5">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="flex items-center gap-2 text-lg font-bold">
            <Newspaper className="h-5 w-5 text-blue-400" />
            Press &amp; Extra Passes
            <span className="text-xs font-normal text-slate-400">
              ({passes.length})
            </span>
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-600/40 bg-slate-700/40 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-700/70"
            >
              <UserPlus className="h-3.5 w-3.5" />
              New pass
            </button>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `This builds a PDF of the ${unprinted} unprinted badge(s) currently listed, two to a sheet, and marks them as printed. Large batches arrive as several files. Continue?`
                  )
                ) {
                  bulkPrint();
                }
              }}
              disabled={bulk || !unprinted}
              className="inline-flex items-center gap-1.5 rounded-md border border-blue-500/30 bg-blue-500/20 px-3 py-1.5 text-xs font-medium text-blue-200 transition-colors hover:bg-blue-500/30 disabled:opacity-50"
            >
              {bulk ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Printer className="h-3.5 w-3.5" />
              )}
              Print {unprinted} unprinted
            </button>
          </div>
        </header>

        {notice && (
          <div className="rounded-lg border border-slate-700/50 bg-slate-800/50 px-3 py-2 text-xs text-slate-300">
            {notice}
          </div>
        )}

        <div className="overflow-hidden rounded-2xl">
          <MaterialReactTable
            columns={columns as any}
            data={passes}
            state={{ isLoading: loading }}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            enableColumnPinning
            mrtTheme={darkMrtTheme}
            initialState={{
              density: "compact",
              pagination: { pageSize: 25, pageIndex: 0 },
              columnPinning: { right: ["actions"] },
            }}
            muiTablePaperProps={{ sx: darkPaperSx }}
            muiTableContainerProps={{ sx: { backgroundColor: "transparent" } }}
            muiTopToolbarProps={{ sx: darkToolbarSx }}
            muiBottomToolbarProps={{ sx: darkToolbarSx }}
            muiTableHeadCellProps={{ sx: darkHeadCellSx }}
            muiTableBodyCellProps={{ sx: darkBodyCellSx }}
            muiTableBodyRowProps={{ sx: darkRowSx }}
            muiSearchTextFieldProps={{ placeholder: "Search…", sx: darkSearchSx }}
          />
        </div>
      </div>

      {badgeFor && (
        <BadgeModal
          pass={badgeFor}
          onClose={() => setBadgeFor(null)}
          onChange={replacePass}
        />
      )}

      {(creating || editing) && (
        <PassModal
          pass={editing}
          categories={categories}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default PressPasses;
